import { useCallback, useEffect } from 'react';
import { useToast } from '@cad-arc';
import { downloadFileWithProgress } from '../../common/helpers/downloads';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { useAttachmentsDownloadStatus } from './use-attachments-download-status';
import { v4 as uuidV4 } from 'uuid';

// threshold for what is considered a "large" file which will immediately show download progress
const LARGE_FILE_THRESHOLD_BYTES = 5 * 1024 * 1024;
// how long we should wait before showing download progress for small files
const SHOW_PROGRESS_DELAY_MS = 750;

/**
 * Creates a progress handler for electron-dl-manager downloads.
 *
 * electron-dl-manager waits for the save dialog to close before downloading.
 */
function createElectronDownloadProgressHandler({
    onProgress,
    onStart,
    onComplete,
    onCancel,
    onError,
    isLargeFile,
}: {
    onProgress: (progress: number) => void;
    onStart: () => void;
    onComplete: () => void;
    onCancel: () => void;
    onError: (errorMessage: string) => void;
    isLargeFile: boolean;
}) {
    let cancelled = false;
    let errored = false;
    let showProgressUI = isLargeFile;
    let progressDelayTimeout: NodeJS.Timeout | null = null;
    let downloadStarted = false;

    const cleanup = () => {
        if (progressDelayTimeout) {
            clearTimeout(progressDelayTimeout);
            progressDelayTimeout = null;
        }
    };

    let currentProgressValue = 0;

    return (update: { progress: number } | { error: string }) => {
        // Ignore all updates after cancellation or error
        if (cancelled || errored) {
            cleanup();
            return;
        }

        // Handle error from electron
        if ('error' in update) {
            errored = true;
            cleanup();
            onError(update.error);
            return;
        }

        currentProgressValue = update.progress;
        // Cancellation signal from electron-dl-manager
        if (currentProgressValue === -1) {
            cancelled = true;
            cleanup();
            onCancel();
            return;
        }

        // For small files, delay showing progress UI to avoid flicker on fast downloads.
        if (currentProgressValue === 0 && !downloadStarted) {
            // Download started (progress === 0 is the start signal after save dialog closes)
            downloadStarted = true;
            onStart();
            if (!isLargeFile) {
                progressDelayTimeout = setTimeout(() => {
                    if (cancelled || errored) {
                        return;
                    }
                    showProgressUI = true;
                    onProgress(currentProgressValue);
                }, SHOW_PROGRESS_DELAY_MS);
            }
            return;
        }

        // Handle completion
        if (currentProgressValue === 100) {
            cleanup();
            onComplete();
            return;
        }

        if (showProgressUI) {
            onProgress(currentProgressValue);
        }
    };
}

type UseDownloadAttachmentProps = {
    attachmentId: number;
    fileName: string;
    fileUrl?: string;
    fileSize?: number;
    onDownloadStart?: () => void;
};
type UseDownloadAttachmentResult = {
    downloadAttachment: () => void;
    isDownloading: boolean;
    progress: number | undefined;
};

/**
 * Hook to handle attachment downloads with progress tracking and toast notifications.
 * Supports both Electron and web environments with appropriate fallbacks.
 *
 * @param attachment - The attachment to download
 * @returns Object containing downloadAttachment function, isDownloading state, and progress
 */
export function useDownloadAttachment({
    attachmentId,
    fileName,
    fileUrl,
    fileSize,
    onDownloadStart,
}: UseDownloadAttachmentProps): UseDownloadAttachmentResult {
    const toast = useToast();
    const { isElectronRewrite } = useApplicationType();
    const { download, isDownloading, setProgress, clearDownload } =
        useAttachmentsDownloadStatus(attachmentId);

    // Cleanup download status on unmount
    useEffect(
        function cleanupDownloadOnUnmount() {
            return () => {
                clearDownload();
            };
        },
        [clearDownload]
    );

    const downloadAttachment = useCallback(
        function handleDownload() {
            if (!fileUrl) {
                return;
            }

            // Use a unique downloadId per click so each request has its own progress subscription.
            // Without this, a second click after cancelling the save dialog can reuse the same key,
            // causing the previous subscription's cleanup to unsubscribe the *new* download's listener.
            // Result: progress stops updating and the Download button stays disabled.
            const downloadId = `${attachmentId}_${uuidV4()}`;
            const isLargeFile = fileSize ? fileSize >= LARGE_FILE_THRESHOLD_BYTES : false;

            if (isElectronRewrite) {
                const progressHandler = createElectronDownloadProgressHandler({
                    onProgress: (progress) => setProgress(fileName, progress),
                    onStart: () => onDownloadStart?.(),
                    onComplete: () => {
                        setProgress(fileName, 100);
                        toast({
                            status: 'default',
                            description: `${fileName} downloaded successfully`,
                            duration: 5_000,
                        });
                        clearDownload();
                    },
                    onCancel: () => {
                        clearDownload();
                    },
                    onError: () => {
                        toast({
                            status: 'error',
                            description: `Failed to download ${fileName}`,
                        });
                        clearDownload();
                    },
                    isLargeFile,
                });

                downloadFileWithProgress(downloadId, fileName, fileUrl, progressHandler, fileSize);
            } else {
                // Web download without progress tracking
                downloadFileWithProgress(downloadId, fileName, fileUrl, () => {}, fileSize);
                onDownloadStart?.();
            }
        },
        [
            attachmentId,
            fileName,
            fileUrl,
            fileSize,
            isElectronRewrite,
            setProgress,
            clearDownload,
            toast,
            onDownloadStart,
        ]
    );

    return {
        downloadAttachment,
        isDownloading,
        progress: download?.progress,
    };
}
